import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeftToLine, ArrowRightToLine } from "lucide-react";
import clsx from "clsx";
import { useCurrentPdf } from "@/views/chat/hooks/useCurrentPdf";
import { ContextFileViewer } from "@/components/feature/layout/left-sidebar/contextFileViewer";
import { Toggle } from "@/components/feature/layout/toggle";
import { useNavigate } from "react-router";
import { FileItem, Item, type Location as FileLocation } from "@/types/fse";
import { FlattenedItem } from "@/views/chat/types";
import { useGetFileDetailsQuery } from "@/api/files";
import { flatFileSystem } from "@/components/feature/layout/left-sidebar/helpers";

interface Props {
  systemId: string;
  onFileSelect: (file: FileItem, isSelected: boolean) => void;
  isVisible: boolean;
  onToggleVisibility: () => void;
  showFolders: boolean;
  showToggleAll?: boolean;
  singleFilesSelection?: boolean;
  initialSelectedFiles?: string[];
}

export const ContextSidebar: React.FC<Props> = React.memo(function ContextSidebar({
  systemId,
  onFileSelect,
  isVisible,
  onToggleVisibility,
  showToggleAll,
  showFolders,
  singleFilesSelection,
  initialSelectedFiles,
}) {
  const { setCurrentPdfId, setSelectedFiles: contextSetSelectedFiles } = useCurrentPdf();
  const navigate = useNavigate();
  const [flattenedItems, setFlattenedItems] = React.useState<FlattenedItem[]>([]);
  const [expandedSummaries, setExpandedSummaries] = React.useState<Set<string>>(new Set());
  const [expandedFolders, setExpandedFolders] = React.useState<Set<string>>(new Set());
  const [selectedFiles, setSelectedFiles] = React.useState<Set<string>>(new Set());

  const { data: rootItem, error, isLoading, refetch: loadFileSystem } = useGetFileDetailsQuery({ fse_id: systemId });

  React.useEffect(() => {
    if (rootItem) {
      const allFolderIds = new Set<string>();
      const collectIds = (item: Item) => {
        if (item.type === "folder") {
          allFolderIds.add(item.id);
          Object.values(item.items).forEach(collectIds);
        }
      };
      collectIds(rootItem);
      setExpandedFolders(allFolderIds);
    }
  }, [rootItem]);

  React.useEffect(() => {
    contextSetSelectedFiles(Array.from(selectedFiles));
  }, [selectedFiles, contextSetSelectedFiles]);

  React.useEffect(() => {
    if (rootItem) {
      const rows = flatFileSystem(rootItem, 0, expandedFolders, showFolders);
      setFlattenedItems(rows);
      const rowsFiles = rows.filter(item => item.type === "file").map(item => item.id);
      if (singleFilesSelection) {
        if (rowsFiles[0]) {
          setSelectedFiles(new Set([rowsFiles[0]]));
        }
      } else {
        if (initialSelectedFiles) {
          setSelectedFiles(new Set(initialSelectedFiles));
        } else {
          setSelectedFiles(new Set(rowsFiles));
        }
      }
    }
  }, [singleFilesSelection, rootItem, expandedFolders, showFolders]);

  const toggleFolder = React.useCallback((folderId: string) => {
    setExpandedFolders(prev => {
      const newExpandedFolders = new Set(prev);
      if (newExpandedFolders.has(folderId)) {
        newExpandedFolders.delete(folderId);
      } else {
        newExpandedFolders.add(folderId);
      }
      return newExpandedFolders;
    });
  }, []);

  const toggleSummary = React.useCallback((fileId: string) => {
    setExpandedSummaries(prev => {
      const newExpandedSummaries = new Set(prev);
      if (newExpandedSummaries.has(fileId)) {
        newExpandedSummaries.delete(fileId);
      } else {
        newExpandedSummaries.add(fileId);
      }
      return newExpandedSummaries;
    });
  }, []);

  const handleFileSelect = React.useCallback(
    (file: FileItem, isSelected: boolean) => {
      onFileSelect(file, isSelected);
      setSelectedFiles(prev => {
        const newSet = singleFilesSelection ? new Set<string>() : new Set(prev);

        if (isSelected || singleFilesSelection) {
          newSet.add(file.id);
        } else {
          newSet.delete(file.id);
        }

        return newSet;
      });
    },
    [onFileSelect, singleFilesSelection],
  );

  const handleFileClick = React.useCallback(
    (file: FileItem) => {
      setCurrentPdfId(file.id);
    },
    [setCurrentPdfId],
  );

  const handleAskFile = React.useCallback(
    (fileId: string) => {
      setCurrentPdfId(fileId);
      navigate(`/chat/${fileId}`);
    },
    [setCurrentPdfId, navigate],
  );

  const getParentLocations = React.useCallback(
    (item: FileItem): FileLocation[] =>
      item.locations.map(location => ({
        id: location.path[location.path.length - 2]?.id || location.id,
        path: location.path.slice(0, -1),
      })),
    [],
  );

  const isAllSelected = flattenedItems.filter(item => item.type === "file").every(item => selectedFiles.has(item.id));

  const handleToggleSelect = (enabled: boolean) => {
    flattenedItems.forEach(item => {
      if (item.type === "file") {
        handleFileSelect(item, enabled);
      }
    });
  };

  return (
    <motion.div
      className={clsx("h-full flex-shrink-0 bg-ui-sidebar-background", {
        "min-w-[280px] max-w-[360px] overflow-y-auto 2xl:max-w-[380px]": isVisible,
        "w-10": !isVisible,
      })}
      initial={{ width: isVisible ? "0px" : "40px" }}
      animate={{ width: isVisible ? "min(20%, 360px)" : "40px" }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between bg-ui-sidebar-background p-2">
          <button onClick={onToggleVisibility} className="rounded p-2 hover:bg-desert-sand-300">
            {isVisible ? <ArrowLeftToLine size={20} /> : <ArrowRightToLine size={20} />}
          </button>
        </div>

        <AnimatePresence>
          {isVisible && (
            <motion.div
              className="flex-grow overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <h2 className="flex p-4 text-lg font-bold">
                Select files
                {showToggleAll && (
                  <div className="ml-auto mt-2 flex-shrink-0">
                    <Toggle enabled={isAllSelected} size="xs" onChange={handleToggleSelect} />
                  </div>
                )}
              </h2>
              <div className="h-[calc(100vh-6rem)] overflow-y-auto px-5">
                <div className="pb-24">
                  <ContextFileViewer
                    loading={isLoading}
                    error={error}
                    rootItem={rootItem}
                    flattenedItems={flattenedItems}
                    loadFileSystem={loadFileSystem}
                    showFolders={showFolders}
                    toggleFolder={toggleFolder}
                    handleFileClick={handleFileClick}
                    handleFileSelect={handleFileSelect}
                    toggleSummary={toggleSummary}
                    expandedSummaries={expandedSummaries}
                    selectedFiles={selectedFiles}
                    handleAskFile={handleAskFile}
                    getParentLocations={getParentLocations}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
});
